import React, { useState, useEffect } from 'react';
import {
    apiGetGroups,
    apiGetEmployeesByGroup,
    apiGetBasicSalaryData,
    apiSaveBasicSalaryData,
    apiGetSalaryCodes,
    apiGetJobCodes,
} from '../../api';
import { InputNumber, message, Select } from 'antd';

const { Option } = Select;

// --- Helper Functions ---
/**
 * Dùng để hiển thị số ở những nơi không thể chỉnh sửa (VD: cột Tổng cộng).
 * Số 0 sẽ được hiển thị là '-'.
 */
const formatDisplayNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    if (Number(value) === 0) return '-';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Dùng cho các ô InputNumber có thể chỉnh sửa.
 * Số 0 sẽ được hiển thị là chuỗi rỗng ('') để người dùng dễ dàng nhập liệu.
 */
const formatInputNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || Number(value) === 0) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const inputParser = (value) => value.replace(/\./g, '');

// --- Component --- 
const BasicSalary = () => {
    // --- State Management ---
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [salaryData, setSalaryData] = useState([]);
    const [salaryCodes, setSalaryCodes] = useState([]);
    const [jobCodes, setJobCodes] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    
    const [selectedMonth, setSelectedMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [selectedEmp, setSelectedEmp] = useState('ALL');

    // --- Data Fetching ---
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoadingGroups(true);
            try {
                const [groupsData, salaryCodesData, jobCodesData] = await Promise.all([
                    apiGetGroups(),
                    apiGetSalaryCodes(),
                    apiGetJobCodes()
                ]);
                setGroups(groupsData);
                setSalaryCodes(salaryCodesData);
                setJobCodes(jobCodesData);
            } catch (error) {
                message.error('Không thể tải dữ liệu cần thiết (bộ phận, mã lương, mã chức vụ).');
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedGroup) {
            handleGroupChange(selectedGroup);
        }
    }, [selectedMonth]);

    // --- Handlers ---
    const handleGroupChange = async (groupid) => {
        setSelectedGroup(groupid);
        if (!selectedMonth) {
            message.warning('Vui lòng chọn tháng trước.');
            setEmployees([]);
            return;
        }
        
        const year = selectedMonth.substring(0, 4);
        const month = selectedMonth.substring(5, 7);
        const monthYearForAPI = `${month}${year}`;

        try {
            setLoadingEmployees(true);
            const data = await apiGetEmployeesByGroup(groupid, monthYearForAPI);
            setEmployees(data);
            setSelectedEmp('ALL');
        } catch (error) {
            message.error('Không thể tải danh sách nhân viên.');
        } finally {
            setLoadingEmployees(false);
        }
    };

    const handleQuery = async () => {
        if (!selectedMonth || !selectedGroup) {
            message.error('Vui lòng chọn tháng và bộ phận.');
            return;
        }
        setLoading(true);
        setIsDataLoaded(false);
        try {
            const year = selectedMonth.substring(0, 4);
            const month = selectedMonth.substring(5, 7);
            const filters = {
                month: `${month}/${year}`,
                groupid: selectedGroup,
                empid: selectedEmp,
                whsno: 'VN'
            };
            const data = await apiGetBasicSalaryData(filters);
            setSalaryData(data);
            if(data.length > 0) {
                 setIsDataLoaded(true);
            } else {
                message.info('Không tìm thấy dữ liệu lương cho lựa chọn này.');
            }
        } catch (error) {
            message.error(`Không thể tải dữ liệu lương: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (value, empid, field) => {
        const newData = salaryData.map(item =>
            item.empid === empid ? { ...item, [field]: value } : item
        );
        setSalaryData(newData);
    };
    
    const handleSalaryCodeChange = (empid, newCode) => {
        const selectedObject = salaryCodes.find(c => c.code === newCode);
        if (selectedObject) {
            const updatedData = salaryData.map(item => {
                if (item.empid === empid) {
                    return { ...item, BB: selectedObject.bonus, LNCODE: newCode };
                }
                return item;
            });
            setSalaryData(updatedData);
        }
    };

    const handleJobCodeChange = (empid, newCode) => {
        const updatedData = salaryData.map(item => {
            if (item.empid === empid) {
                return { ...item, Job: newCode };
            }
            return item;
        });
        setSalaryData(updatedData);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const year = selectedMonth.substring(0, 4);
            const month = selectedMonth.substring(5, 7);
            const monthYearForAPI = `${month}/${year}`;

            const dataToSave = salaryData.map(row => ({
                ...row,
                DM: 'VND',
                MEMO: null,
                WP: 0,
            }));

            await apiSaveBasicSalaryData({ month: monthYearForAPI, whsno: 'VN', data: dataToSave });
            
            message.success('Đã ghi vào cơ sở dữ liệu thành công!');

            // Xóa dữ liệu và ẩn bảng sau khi lưu thành công
            setSalaryData([]);
            setIsDataLoaded(false);

        } catch (error) {
            message.error(`Lưu dữ liệu thất bại: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // --- Table Definitions ---
    const tableHeaders = [
        { key: 'empid', label: 'MSNV', isFixed: 'left', width: '100px', style: { left: 0 } },
        { key: 'name', label: 'Họ và Tên', isFixed: 'left', width: '200px', style: { left: '100px' } },
        { key: 'jobCode', label: 'Mã Chức Vụ', width: '180px' },
        { key: 'salaryCode', label: 'Mã Lương', width: '180px' },
        { key: 'BB', label: 'LƯƠNG CB', isNumeric: true, width: '130px' },
        { key: 'btien', label: 'BÙ LƯƠNG', isNumeric: true, width: '130px' },
        { key: 'CV', label: 'PC CHỨC VỤ', isNumeric: true, width: '130px' },
        { key: 'PHU', label: 'PC ĐIỆN THOẠI', isNumeric: true, width: '130px' },
        { key: 'NN', label: 'PC XĂNG XE', isNumeric: true, width: '130px' },
        { key: 'KT', label: 'PC KỸ THUẬT', isNumeric: true, width: '130px' },
        { key: 'MT', label: 'PC MÔI TRƯỜNG', isNumeric: true, width: '130px' },
        { key: 'TTKH', label: 'PC NHÀ Ở', isNumeric: true, width: '130px' },
        { key: 'QC', label: 'CHUYÊN CẦN', isNumeric: true, width: '130px' },
        { key: 'total', label: 'TỔNG CỘNG', isFixed: 'right', width: '150px', style: { right: 0 }, isNumeric: true },
    ];

    // --- Render ---
    return (
        <div style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">XÁC NHẬN & LƯU LƯƠNG CƠ BẢN</h2>

            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                <div>
                    <label htmlFor="salary-month" className="block text-sm font-medium text-gray-700">Chọn tháng</label>
                    <input
                        type="month"
                        id="salary-month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="mt-1 p-2 border rounded-md"
                        style={{ fontFamily: 'inherit' }}
                    />
                </div>
                <div>
                    <label htmlFor="group-select" className="block text-sm font-medium text-gray-700">Chọn bộ phận</label>
                    <select
                        id="group-select"
                        value={selectedGroup}
                        onChange={(e) => handleGroupChange(e.target.value)}
                        disabled={loadingGroups}
                        className="mt-1 block w-52 p-2 border rounded-md"
                        style={{ fontFamily: 'inherit' }}
                    >
                        <option value="ALL">Tất cả bộ phận</option>
                        {groups.map(g => (
                            <option key={g.groupId} value={g.groupId}>
                                {g.groupName} ({g.groupId})
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="emp-select" className="block text-sm font-medium text-gray-700">Chọn nhân viên</label>
                    <Select
                        id="emp-select"
                        showSearch
                        value={selectedEmp}
                        onChange={(value) => setSelectedEmp(value)}
                        disabled={loadingEmployees || !employees.length}
                        style={{ width: 240, fontFamily: 'inherit' }}
                        placeholder="Tìm và chọn nhân viên"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const optionText = Array.isArray(option.children) ? option.children.join('') : option.children;
                            return (optionText || '').toLowerCase().includes(input.toLowerCase());
                        }}
                        className="mt-1"
                    >
                        <Option value="ALL">Tất cả nhân viên</Option>
                        {employees.map(e => (
                            <Option key={e.empid} value={e.empid}>
                                {e.name} ({e.empid})
                            </Option>
                        ))}
                    </Select>
                </div>
                <button onClick={handleQuery} disabled={loading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                    {loading ? 'Đang tải...' : 'Tra cứu'}
                </button>
            </div>

            {isDataLoaded && (
                <>
                    <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '70vh' }}>
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr>
                                    {tableHeaders.map(header => (
                                        <th key={header.key} 
                                            className={`p-2 font-semibold text-gray-600 whitespace-nowrap border-b border-r sticky top-0 bg-gray-100 ${header.isFixed ? 'z-30' : 'z-20'} ${header.isNumeric ? 'text-right' : 'text-left'}`}
                                            style={{ width: header.width, minWidth: header.width, ...header.style }}>
                                            {header.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    <tr><td colSpan={tableHeaders.length} className="p-4 text-center">Đang tải dữ liệu...</td></tr>
                                ) : salaryData.map(row => {
                                    const total = (row.BB || 0) + (row.CV || 0) + (row.PHU || 0) + (row.NN || 0) + (row.KT || 0) + (row.MT || 0) + (row.TTKH || 0) + (row.QC || 0) + (row.btien || 0);
                                    return (
                                        <tr key={row.empid} className="hover:bg-gray-50">
                                            {tableHeaders.map(header => (
                                                <td key={`${row.empid}-${header.key}`}
                                                    className={`
                                                        whitespace-nowrap border-r
                                                        ${header.isFixed ? 'sticky z-10' : ''}
                                                        ${header.isNumeric ? 'p-0' : 'p-0'}
                                                        ${header.key === 'total' 
                                                            ? 'bg-gray-100 font-semibold text-blue-700' 
                                                            : (header.isFixed ? 'bg-white hover:bg-gray-50' : '')
                                                        }
                                                        ${header.isNumeric ? 'text-right' : 'text-left'}
                                                    `}
                                                    style={{ ...header.style }}>
                                                    {(() => {
                                                        if (header.key === 'total') return <div className="p-2">{formatDisplayNumber(total)}</div>;

                                                        if (header.key === 'salaryCode') {
                                                            return <Select bordered={false} style={{width: '100%', fontFamily: 'inherit'}} value={row.LNCODE} onChange={(v) => handleSalaryCodeChange(row.empid, v)} >{salaryCodes.map(c => <Option key={c.code} value={c.code}>{`${c.code} - ${formatDisplayNumber(c.bonus)}`}</Option>)}</Select>;
                                                        }

                                                        if (header.key === 'jobCode') {
                                                            return <Select bordered={false} style={{width: '100%', fontFamily: 'inherit'}} value={row.Job} onChange={(v) => handleJobCodeChange(row.empid, v)} >{jobCodes.map(c => <Option key={c.code} value={c.code}>{c.code}</Option>)}</Select>;
                                                        }

                                                        if (header.isNumeric) {
                                                            return <InputNumber value={row[header.key]} onChange={v => handleValueChange(v, row.empid, header.key)} bordered={false} style={{ width: '100%', fontFamily: 'inherit', backgroundColor: 'transparent' }} className="text-right" formatter={formatInputNumber} parser={inputParser} controls={false}/>;
                                                        }

                                                        return <div className="p-2">{row[header.key]}</div>;
                                                    })()}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end mt-6">
                        <button onClick={handleSave} disabled={isSaving || loading} className="px-8 py-3 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 text-base">
                            {isSaving ? 'Đang lưu...' : 'Xác nhận & Lưu'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default BasicSalary;

